import * as React from "react";
import styles from "./styles.module.css";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getPatientGeneralDetails,
  getPatientReadings,
} from "../../../services/patient/details";
import { getAuditReport } from "../../../services/case-manager/billings";
import { formatDate, formatSecondsToTime } from "../../../utils/helper";
import { formatNaiveDateTime } from "../../../utils/dateFormatters";
import { useLocation } from "react-router-dom";
import HealthChart from "../../shared-modules/HealthChart";
import Button from "../../button-v1";
import { PDFDocument } from "pdf-lib";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import Spinner from "../../spinner";
import { shareAuditReport } from "../../../services/patient/documents";
const ReadingView = () => {
  const [queryParams] = useSearchParams();
  const [patientId, setPatientId] = React.useState(null);
  const [date, setDate] = React.useState(null);
  const [email, setEmail] = React.useState(null);

  const {
    data: patient,
    error,
    isLoading,
    refetch,
  } = useQuery(["patient-general-details", patientId], () =>
    getPatientGeneralDetails(patientId)
  );

  const shareAuditReportMutation = useMutation(({ file }) => {
    const oldFormat = date.split("-");
    const newDate = `${oldFormat[0]}/${oldFormat[1]}`;
    shareAuditReport(file, email, patientId, newDate);
  });

  const {
    data: auditReport,
    isLoading: isReadingLoading,
    error: readingError,
  } = useQuery(["patient-readings", patientId, date], () =>
    getAuditReport(patientId, date)
  );

  React.useEffect(() => {
    if (queryParams) {
      setPatientId(queryParams.get("patientId"));
      setDate(queryParams.get("date"));
      setEmail(queryParams.get("email"));
    }
  }, [queryParams]);

  if (isLoading || isReadingLoading) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <section className={styles.download_button}>
        <Button
          id="download-btn"
          onClick={async () => {
            const binary = await downloadFullPageAsPDF(
              document.getElementById("reading-pdf-view"),
              patient?.user?.first_name,
              date
            );
            shareAuditReportMutation.mutate({ file: binary });
          }}
        >
          Download
        </Button>
      </section>

      <main className={styles.container} id="reading-pdf-view">
        <section className={styles.header}>
          <img src="/assets/images/th_logo.png" />
          <div className={styles.separator}></div>
          <p className={styles.location}>{patient?.location?.name ?? "_"}</p>
        </section>
        <section className={styles.patientInfo}>
          <section className="label-container">
            <label className={styles.label}>Patient Name</label>
            <p className={styles.label_value}>
              {patient?.user?.first_name} {patient?.user?.last_name}
            </p>
          </section>

          {/* <section className="label-container">
            <label className={styles.label}>Chart Type</label>
            <p className={styles.label_value}>reading Type</p>
          </section> */}

          <section className="label-container">
            <label className={styles.label}>Report Month</label>
            <p className={styles.label_value}>{auditReport?.report_month}</p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Birthday</label>
            <p className={styles.label_value}>
              {formatDate(patient?.user?.dob)}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Enrollment Date</label>
            <p className={styles.label_value}>
              {formatDate(auditReport?.patient?.enrollment_date ?? "_")}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Consent Date</label>
            <p className={styles.label_value}>
              {formatDate(auditReport?.patient?.consent_date ?? "_")}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Case Manager</label>
            <p className={styles.label_value}>
              {auditReport?.patient?.case_manager ?? "_"}
            </p>
          </section>
          <section className="label-container">
            <label className={styles.label}>Email</label>
            <p className={styles.label_value}>{patient?.user?.email ?? "_"}</p>
          </section>
          <section className="label-container">
            <label className={styles.label}>Provider</label>
            <p className={styles.label_value}>
              {auditReport?.patient?.provider ?? "_"}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Preferred Language</label>
            <p className={styles.label_value}>
              {patient?.preferred_language ?? "_"}
            </p>
          </section>
          <section className="label-container">
            <label className={styles.label}>Address Line 1</label>
            <p className={styles.label_value}>
              {patient?.user?.address_line_1 ?? "_"}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Zipcode</label>
            <p className={styles.label_value}>
              {patient?.user?.postal_code ?? "_"}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>RPM Device Type</label>
            <p className={styles.label_value}>
              {auditReport?.["RPM Device Type"] || "_"}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>RPM Device Serial</label>
            <p className={styles.label_value}>
              {auditReport?.["RPM Device Serial"] ?? "_"}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Days of Reading</label>
            <p className={styles.label_value}>
              {auditReport?.reading_days_count ?? "_"}
            </p>
          </section>
          <section className="label-container">
            <label className={styles.label}>Total RPM time</label>
            <p className={styles.label_value}>
              {formatSecondsToTime(auditReport?.total_rpm_time)}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Referring Organization</label>
            <p className={styles.label_value}>
              {patient?.location?.name ?? "_"}
            </p>
          </section>

          <section className="label-container">
            <label className={styles.label}>Facility</label>
            <p className={styles.label_value}>{patient?.user?.city ?? "_"}</p>
          </section>
        </section>
        <div className={styles.separator}></div>
        <h4>Readings</h4>
        <section className={styles.table_container}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Reading</th>
              </tr>
            </thead>
            <tbody>
              {auditReport?.readings?.map((reading) => (
                <tr key={reading.id} className={styles.tableRow}>
                  <td className={`${styles.tableCell} ${styles.dateCell}`}>
                    {formatDate(reading?.recorded_at ?? "_")}
                  </td>

                  <td className={`${styles.tableCell} ${styles.dateCell}`}>
                    {reading?.reading_type ?? "_"}
                  </td>

                  <td className={`${styles.tableCell} ${styles.readingCell}`}>
                    {reading.systolic || reading.diastolic
                      ? `${reading.systolic ?? 0}/${
                          reading.diastolic ?? 0
                        } mmHg`
                      : reading.weight
                      ? `${reading.weight} kg`
                      : reading.temperature
                      ? `${reading.temperature} F`
                      : reading.glucose
                      ? `${reading.glucose} mg/dl`
                      : reading.oxygen || reading.heart_rate
                      ? `${reading.oxygen ?? 0}% /${
                          reading.heart_rate ?? 0
                        } bpm`
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <h4>Notes</h4>
        <section className={styles.table_container}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Date</th>
                <th>Text</th>
                <th>Creator</th>
              </tr>
            </thead>
            <tbody>
              {auditReport?.notes?.map((note) => (
                <tr key={note.id} className={styles.tableRow}>
                  <td className={`${styles.tableCell} ${styles.dateCell}`}>
                    {formatDate(note?.created_at)}
                  </td>
                  <td className={`${styles.tableCell} ${styles.typeCell}`}>
                    {note?.note_text}
                  </td>
                  <td className={`${styles.tableCell} ${styles.typeCell}`}>
                    {note?.assigned_by_name ?? "_"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <h4>Sessions</h4>
        <section className={styles.table_container}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Date</th>
                <th>Duration</th>
                <th>Staff</th>
              </tr>
            </thead>
            <tbody>
              {auditReport?.monitoring_sessions?.map((session) => (
                <tr key={session.id} className={styles.tableRow}>
                  <td className={`${styles.tableCell} ${styles.dateCell}`}>
                    {formatNaiveDateTime(session?.created_at)}
                  </td>
                  <td className={`${styles.tableCell} ${styles.typeCell}`}>
                    <p>
                      {formatSecondsToTime(session?.total_rpm_seconds)}/
                      {formatSecondsToTime(session?.total_ccm_seconds)}
                    </p>
                  </td>
                  <td className={`${styles.tableCell} ${styles.typeCell}`}>
                    {session?.staff_member?.full_name ?? "_"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <h4>SMS Messages</h4>
        <section className={styles.table_container}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Reading</th>
              </tr>
            </thead>
            <tbody>
              {/* {readings?.map((reading) => (
                <tr key={reading.id} className={styles.tableRow}>
                  <td className={`${styles.tableCell} ${styles.dateCell}`}>
                    {formatDate(reading.recorded_at)}
                  </td>
                  <td className={`${styles.tableCell} ${styles.typeCell}`}>
                    {reading.reading_type_display}
                  </td>
                  <td className={`${styles.tableCell} ${styles.readingCell}`}>
                    {reading.systolic || reading.diastolic
                      ? `${reading.systolic ?? 0}/${
                          reading.diastolic ?? 0
                        } mmHg`
                      : reading.weight
                      ? `${reading.weight} kg`
                      : reading.temperature
                      ? `${reading.temperature} F`
                      : reading.glucose
                      ? `${reading.glucose} mg/dl`
                      : reading.oxygen || reading.heart_rate
                      ? `${reading.oxygen ?? 0}% /${
                          reading.heart_rate ?? 0
                        } bpm`
                      : "-"}
                  </td>
                </tr>
              ))} */}
            </tbody>
          </table>
        </section>
      </main>
    </>
  );
};

async function downloadFullPageAsPDF(element) {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    removeContainer: true,
    ignoreElements: (el) =>
      el.style.display === "none" || el.tagName.toLowerCase() === "script",
  });

  const A4_WIDTH_PT = 595.28;
  const A4_HEIGHT_PT = 841.89;

  const pxPerPt = canvas.width / A4_WIDTH_PT;
  const marginPt = 10;
  const marginPx = marginPt * pxPerPt;

  const contentHeightPx = A4_HEIGHT_PT * pxPerPt - 2 * marginPx;
  const contentWidthPt = A4_WIDTH_PT - 2 * marginPt;

  const pdfDoc = await PDFDocument.create();

  let yPx = 0;
  const totalHeightPx = canvas.height;

  while (yPx < totalHeightPx) {
    const remainingPx = totalHeightPx - yPx;
    const isLast = remainingPx <= contentHeightPx;

    const sliceHeightPx = Math.min(contentHeightPx, remainingPx);
    const sliceHeightPt = sliceHeightPx / pxPerPt;

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;

    const ctx = sliceCanvas.getContext("2d");
    ctx.drawImage(
      canvas,
      0,
      yPx,
      canvas.width,
      sliceHeightPx,
      0,
      0,
      canvas.width,
      sliceHeightPx
    );

    const imgData = sliceCanvas.toDataURL("image/png");
    const pngImage = await pdfDoc.embedPng(imgData);
    const page = pdfDoc.addPage([A4_WIDTH_PT, A4_HEIGHT_PT]);

    const drawY = isLast
      ? A4_HEIGHT_PT - marginPt - sliceHeightPt // align to top
      : marginPt; // center normal slices

    page.drawImage(pngImage, {
      x: marginPt,
      y: drawY,
      width: contentWidthPt,
      height: sliceHeightPt,
    });

    yPx += sliceHeightPx;
  }

  const pdfBytes = await pdfDoc.save({
    useObjectStreams: false,
    addDefaultPage: false,
  });

  const blob = new Blob([pdfBytes], {
    type: "application/pdf",
  });

  return blob;
}

export default ReadingView;
